import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { AreaChart, Area, ResponsiveContainer } from 'recharts';
import {
  TrendingUp,
  TrendingDown,
  Minus,
  Building2,
  Wallet,
  PiggyBank,
  CreditCard,
  Home,
  Car,
  Landmark,
  Coins,
  Watch,
  Gem,
  Bitcoin,
  Plus,
} from 'lucide-react';
import { useCurrency } from '@/contexts';
import { useTranslation } from '@/hooks/useTranslation';
import { ACCOUNT_GROUPS } from '@/hooks/useAccounts';
import { AccountSourceBadge } from '@/components/AccountSourceBadge';
import type { Database } from '@/lib/database.types';

type Account = Database['public']['Tables']['accounts']['Row'];

interface SparklinePoint {
  date: string;
  value: number;
}

interface AccountSparklineData {
  points: SparklinePoint[];
  trend: 'up' | 'down' | 'neutral';
  changePercent: number;
  changeAmount: number;
}

interface AccountCardsProps {
  accounts: Account[];
  sparklineData: Map<string, AccountSparklineData>;
  isLoading?: boolean;
}

// Icon mapping for account subtypes
const ACCOUNT_ICONS: Record<string, React.ElementType> = {
  checking: Building2,
  joint_account: Building2,
  savings: PiggyBank,
  money_market: PiggyBank,
  cash: Wallet,
  investment: Landmark,
  real_estate: Home,
  vehicle: Car,
  watch: Watch,
  jewelry: Gem,
  precious_metal: Coins,
  crypto: Bitcoin,
  pea_stock: Landmark,
  credit_card: CreditCard,
  mortgage: Home,
  loan: CreditCard,
  other_asset: Wallet,
  other_liability: CreditCard,
};

// Color mapping for account types
const ACCOUNT_COLORS: Record<string, string> = {
  checking: '#3b82f6',
  joint_account: '#3b82f6',
  savings: '#16c47f',
  money_market: '#16c47f',
  cash: '#f59e0b',
  investment: '#8b5cf6',
  real_estate: '#06b6d4',
  vehicle: '#64748b',
  watch: '#f97316',
  jewelry: '#ec4899',
  precious_metal: '#eab308',
  crypto: '#f97316',
  pea_stock: '#8b5cf6',
  credit_card: '#ef4444',
  mortgage: '#ef4444',
  loan: '#ef4444',
  other_asset: '#94a3b8',
  other_liability: '#94a3b8',
};

function AccountCard({
  account,
  sparklineData,
}: {
  account: Account;
  sparklineData?: AccountSparklineData;
}) {
  const navigate = useNavigate();
  const { formatAmountInCurrency, isSecretMode } = useCurrency();

  const Icon = ACCOUNT_ICONS[account.subtype] || Wallet;
  const color = ACCOUNT_COLORS[account.subtype] || '#94a3b8';
  const isLiability = account.type === 'liability';
  const displayBalance = isLiability ? -Math.abs(account.balance) : account.balance;
  const accountCurrency = account.currency || 'EUR';

  const TrendIcon =
    sparklineData?.trend === 'up'
      ? TrendingUp
      : sparklineData?.trend === 'down'
        ? TrendingDown
        : Minus;

  return (
    <div
      onClick={() => navigate(`/accounts/${account.id}`)}
      className="group relative bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700/50 p-3 hover:shadow-md hover:shadow-gray-200/50 dark:hover:shadow-black/30 hover:border-gray-200 dark:hover:border-gray-600 transition-all duration-200 cursor-pointer"
    >
      <div className="flex items-center gap-2.5">
        {/* Account Icon */}
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: `${color}15` }}
        >
          <Icon className="w-4 h-4" style={{ color }} />
        </div>

        {/* Account Info */}
        <div className="flex-1 min-w-0">
          <p className="flex items-center gap-1.5 text-xs font-semibold text-gray-900 dark:text-white">
            <span className="truncate">{account.name}</span>
            <AccountSourceBadge source={account.source} compact />
          </p>
          <p className="text-sm font-bold tabular-nums text-gray-900 dark:text-white">
            {isSecretMode ? '•••' : formatAmountInCurrency(displayBalance, accountCurrency)}
          </p>
        </div>

        {/* Sparkline Chart */}
        <div className="w-16 h-8 flex-shrink-0">
          {sparklineData && sparklineData.points.length > 1 ? (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={sparklineData.points}>
                <defs>
                  <linearGradient id={`sparkGrad-${account.id}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={color} stopOpacity={0.3} />
                    <stop offset="100%" stopColor={color} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <Area
                  type="monotone"
                  dataKey="value"
                  stroke={color}
                  strokeWidth={1.5}
                  fill={`url(#sparkGrad-${account.id})`}
                  dot={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Minus className="w-4 h-4 text-gray-300 dark:text-gray-600" />
            </div>
          )}
        </div>

        {/* Trend Indicator */}
        <div
          className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold ${
            sparklineData?.trend === 'up'
              ? 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400'
              : sparklineData?.trend === 'down'
                ? 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400'
                : 'bg-gray-50 dark:bg-gray-700 text-gray-400'
          }`}
        >
          <TrendIcon className="w-3 h-3" />
          <span>
            {sparklineData?.changePercent !== undefined
              ? `${sparklineData.changePercent > 0 ? '+' : ''}${sparklineData.changePercent}%`
              : '0%'}
          </span>
        </div>
      </div>
    </div>
  );
}

export function AccountCards({ accounts, sparklineData, isLoading = false }: AccountCardsProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Group accounts by type
  const groupedAccounts = useMemo(() => {
    const groups: { key: string; label: string; accounts: Account[] }[] = [];

    const allGroups = [
      ...ACCOUNT_GROUPS.asset.map((g) => ({ ...g, type: 'asset' as const })),
      ...ACCOUNT_GROUPS.liability.map((g) => ({ ...g, type: 'liability' as const })),
    ];

    for (const group of allGroups) {
      const groupAccounts = accounts.filter(
        (a) => a.type === group.type && group.subtypes.includes(a.subtype as never)
      );
      if (groupAccounts.length > 0) {
        groups.push({
          key: group.key,
          label: t(`accounts.group.${group.key}` as any),
          accounts: groupAccounts,
        });
      }
    }

    return groups;
  }, [accounts, t]);

  if (isLoading) {
    return (
      <div className="space-y-2">
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700/50 p-3 animate-pulse"
          >
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded-lg" />
              <div className="flex-1">
                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-24 mb-1.5" />
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-20" />
              </div>
              <div className="w-16 h-8 bg-gray-200 dark:bg-gray-700 rounded" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (accounts.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700/50 p-8 text-center">
        <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <Wallet className="w-8 h-8 text-gray-400" />
        </div>
        <p className="text-gray-500 dark:text-gray-400 mb-4">
          {t('dashboard.accounts.empty' as any)}
        </p>
        <button
          onClick={() => navigate('/accounts')}
          className="inline-flex items-center gap-2 px-4 py-2 bg-[#16c47f] hover:bg-[#12b070] text-white rounded-lg text-sm font-medium transition-colors"
        >
          <Plus className="w-4 h-4" />
          {t('dashboard.accounts.add' as any)}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {groupedAccounts.map((group) => (
        <div key={group.key}>
          <p className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-[0.1em] mb-1.5 px-1">
            {group.label}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {group.accounts.map((account) => (
              <AccountCard
                key={account.id}
                account={account}
                sparklineData={sparklineData.get(account.id)}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
